/**
 * The bridge between content attributes and post-enrichment step flags.
 *
 * Two of the Conversational attributes are not enrichment *fields* — they name
 * POST-phase steps that `UseCaseConfig` also exposes as booleans
 * (`searchDescriptorsEnabled` / `phraseDescriptionsEnabled`). The experiment
 * wizard therefore has one control, the attribute chip, and derives the boolean
 * from it. Two independent controls for one outcome on one screen would disagree
 * the moment either moved.
 */

import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';

/** Agent catalog key → the flag it sets. Keys are the agent's `field_name`, which
 *  is what the draft PATCH already sends as `contentAttributes`. */
const STEP_ATTRIBUTE_FLAGS = {
  search_descriptors: 'searchDescriptorsEnabled',
  phrase_descriptions: 'phraseDescriptionsEnabled',
} as const satisfies Record<string, keyof EnrichmentPostStepFlags>;

/**
 * Post-step flags an experiment always sends, regardless of UI.
 *
 * Scoring is **explicitly false**, not omitted. An experiment already scores in
 * dedicated stages — content-scoring before the pipeline and `PostScoring` inside
 * it — so the in-process scorer would score every product a second time, uncapped.
 * Omitting the keys would leave that to the server env default, which could switch
 * it on; sending `false` says no. There is no UI for these because there is no
 * legitimate reason to turn them on here.
 */
export const EXPERIMENT_POST_STEP_DEFAULTS: EnrichmentPostStepFlags = {
  scoringPreEnabled: false,
  scoringPostEnabled: false,
};

/**
 * Attribute keys that start **unselected**, against the card's otherwise
 * everything-on default.
 *
 * These name POST-phase enrichment steps rather than fields, and the flags doc
 * treats them as opt-in for every other flow — the toggles default off there. If
 * they defaulted on here, activating them in the config service would silently
 * turn the steps on for every new experiment, which is a run-cost change nobody
 * asked for.
 */
export const OPT_IN_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set(
  Object.keys(STEP_ATTRIBUTE_FLAGS),
);

/**
 * Sub-attribute dependencies, dependent key → the key it needs.
 *
 * Mirrors `resolveEnrichmentFlagDependencies`: PHRASE_DESCRIPTIONS consumes
 * SEARCH_DESCRIPTORS output, and the server reports a misconfiguration rather
 * than rejecting the pair, so the client is the only guard. Keyed by catalog key
 * because ids are per-environment while keys are the stable contract.
 */
export const CONTENT_ATTRIBUTE_DEPENDENCIES: Readonly<Record<string, string>> = {
  phrase_descriptions: 'search_descriptors',
};

/**
 * Derives the post-step flags implied by the selected content-attribute keys.
 *
 * Only emits a key that is actually present in the catalog selection: a tenant
 * whose config service predates these attributes selects neither, so nothing is
 * sent and the server default applies — rather than an explicit `false` that
 * would override it.
 */
export function postStepFlagsFromContentAttributes(
  selectedKeys: readonly string[],
  availableKeys: readonly string[],
): EnrichmentPostStepFlags {
  const selected = new Set(selectedKeys);
  const available = new Set(availableKeys);
  const flags: EnrichmentPostStepFlags = {};

  for (const [attributeKey, flagKey] of Object.entries(STEP_ATTRIBUTE_FLAGS)) {
    if (!available.has(attributeKey)) continue;
    flags[flagKey] = selected.has(attributeKey);
  }

  return flags;
}
